import { useEffect, useState } from "react";
import { Box, Typography, Button, Divider } from "@mui/material";
import AlternativeEmailField from "../../../components/alternative-email/alternative-email";
import AlternativePhoneField from "../../../components/alternative-phone/alternative-phone";
import DialogContainer from "../../../components/dialog-container/dialog-container";
import TextInput from "../../../components/textinput/textinput";
import { phoneFormatInput } from "../../../utils/Formatter";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useFormik } from "formik";
import * as yup from "yup";
import { updateCurrentUserRequest } from "../../../utils/APICalls";
import { useDispatch } from "react-redux";
import { currentUserRequest } from "../../../redux/users/slice";
import Loading from "../../../components/loading/loading";
import ErrorNotification from "../../../components/error-notification/error-notification";
import { emailRegex } from "../../../utils/Constant";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

interface FormValues {
  title?: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
}

const ValidationSchema = yup.object({
  // title: yup.string().required("This field can't be empty"),
  firstName: yup.string().required("This field can't be empty"),
  lastName: yup.string().required("This field can't be empty"),
  userName: yup.string().required("This field can't be empty"),
});

export default function EditMyProfile({
  showDialog,
  closeDialog,
  data,
}: props) {
  const [selectedEmail, setSelectedEmail] = useState("altEmail0");
  const [selectedPhone, setSelectedPhone] = useState("altPhone0");
  const [emails, setEmails] = useState<any[]>([]);
  const [phones, setPhones] = useState<any[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [isEmailEmptyError, setIsEmailEmptyError] = useState(false);
  const [isPhoneEmptyError, setIsPhoneEmptyError] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();

  useEffect(() => {
    if (data?.emails) {
      const allEmails = [];
      const primaryList = data.emails.filter((e: any) => e.isPrimary);
      if (primaryList.length > 0) {
        allEmails.push(primaryList[0]);
      }
      const alternativeList = data.emails.filter(
        (e: any) => e.isPrimary === false
      );
      if (alternativeList.length > 0) {
        allEmails.push(...alternativeList);
      }
      setEmails(allEmails);
    }
    if (data?.phones) {
      const allPhones = [];
      const primaryList = data.phones.filter((e: any) => e.isPrimary);
      if (primaryList.length > 0) {
        allPhones.push(primaryList[0]);
      }
      const alternativeList = data.phones.filter(
        (e: any) => e.isPrimary === false
      );
      if (alternativeList.length > 0) {
        allPhones.push(...alternativeList);
      }
      setPhones(allPhones);
    }
  }, [data]); // eslint-disable-line

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      title: data?.title || "",
      firstName: data?.firstName || "",
      lastName: data?.lastName || "",
      userName: data?.username || "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (value) => {
      if (isEmailEmpty()) {
        setIsEmailEmptyError(true);
        return;
      }
      if (isPhoneEmpty()) {
        setIsPhoneEmptyError(true);
        return;
      }
      if (isEmailDuplicate()) {
        return;
      }
      if (isPhoneDuplicate()) {
        return;
      }
      if (checkEmailInValid()) {
        return;
      }
      const payload = {
        title: value?.title,
        firstName: value?.firstName,
        lastName: value?.lastName,
        username: value?.userName,
        // isActive: data?.isActive,
        emails: emails,
        phones: phones,
      };
      updateUserDetails(payload);
    },
  });

  const isEmailEmpty = () => {
    let isEmpty = false;
    emails.forEach((email) => {
      if (!email.emailAddress) isEmpty = true;
    });
    return isEmpty;
  };

  const isEmailDuplicate = () => {
    let isDuplicate = false;
    const valueArr = emails.map((item) => item.emailAddress);
    isDuplicate = valueArr.some((item, idx) => valueArr.indexOf(item) !== idx);
    return isDuplicate;
  };

  const isPhoneEmpty = () => {
    let isEmpty = false;
    phones.forEach((phone) => {
      if (!phone.phoneNumber) isEmpty = true;
    });
    return isEmpty;
  };

  const isPhoneDuplicate = () => {
    let isDuplicate = false;
    const valueArr = phones.map((item) => item.phoneNumber);
    isDuplicate = valueArr.some((item, idx) => valueArr.indexOf(item) !== idx);
    return isDuplicate;
  };

  const checkEmailInValid = () => {
    let isEmailInValid = false;
    emails.forEach((email: any) => {
      if (!emailRegex.test(email?.emailAddress)) {
        isEmailInValid = true;
      }
    });
    return isEmailInValid;
  };

  const handleSelectedEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    setSelectedEmail(event.target.value);

    const allEmails = JSON.parse(JSON.stringify(emails));
    if (selectedIndex >= 0) {
      // if radio selected set to primary
      allEmails.forEach((allEmail: any, index: number) => {
        if (index === selectedIndex) {
          allEmail.isPrimary = true;
        } else {
          allEmail.isPrimary = false;
        }
      });
    }
    setEmails(allEmails);
  };

  const onEmailChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const allEmails = JSON.parse(JSON.stringify(emails));
    allEmails[index].emailAddress = e.target.value;
    setEmails(allEmails);
  };

  const addEmailClick = () => {
    setEmails((prev) => [...prev, { emailAddress: "", isPrimary: false }]);
  };

  const deleteEmailCLick = (index: number) => {
    const allEmails = [...emails];
    allEmails.splice(index, 1);
    setEmails(allEmails);

    //if delete email is above selected email then reset selectedEmail by -1
    const selectedEmailIndex = Number(
      selectedEmail.substring(selectedEmail.length - 1)
    );
    if (selectedEmailIndex > index)
      setSelectedEmail(`altEmail${selectedEmailIndex - 1}`);
  };

  const handleSelectedPhoneChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    setSelectedPhone(event.target.value);

    const allPhones = JSON.parse(JSON.stringify(phones));
    if (selectedIndex >= 0) {
      // if radio selected set to primary
      allPhones.forEach((allPhone: any, index: number) => {
        if (index === selectedIndex) {
          allPhone.isPrimary = true;
        } else {
          allPhone.isPrimary = false;
        }
      });
    }
    setPhones(allPhones);
  };

  const onPhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const allPhones = JSON.parse(JSON.stringify(phones));
    allPhones[index].phoneNumber = phoneFormatInput(e.target.value);
    setPhones(allPhones);
  };

  const addPhoneClick = () => {
    setPhones((prev) => [...prev, { phoneNumber: "", isPrimary: false }]);
  };

  const deletePhoneClick = (index: number) => {
    const allPhones = [...phones];
    allPhones.splice(index, 1);
    setPhones(allPhones);

    //if delete phone is above selected phone then reset selectedEmail by -1
    const selectedPhoneIndex = Number(
      selectedPhone.substring(selectedPhone.length - 1)
    );
    if (selectedPhoneIndex > index)
      setSelectedPhone(`altPhone${selectedPhoneIndex - 1}`);
  };

  const updateUserDetails = async (payload: any) => {
    setShowLoading(true);
    const updateUserResponse = await updateCurrentUserRequest(payload);
    setShowLoading(false);
    if (updateUserResponse?.status === 200) {
      dispatch(currentUserRequest());
      closeDialog();
    } else if (updateUserResponse?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(updateUserResponse?.data?.detail);
    }
  };

  return (
    <DialogContainer
      title="Edit User Details"
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box>
          <Box className="user-name-layout">
            <Typography className="label-txt">Title</Typography>
            <TextInput
              className="field-input"
              id="title"
              name="title"
              label="Title"
              variant="outlined"
              value={formik.values.title}
              placeholder="Title"
              onChange={formik.handleChange}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
            />
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="label-txt">Name</Typography>
            <Box sx={{ display: "flex", width: "calc(100% - 180px)" }}>
              <TextInput
                sx={{ flex: 1, mr: 2 }}
                id="first-name"
                name="firstName"
                label="First Name"
                variant="outlined"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
              <TextInput
                sx={{ flex: 1 }}
                id="last-name"
                name="lastName"
                label="Last Name"
                variant="outlined"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Box>
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="label-txt">Username</Typography>
            <TextInput
              className="field-input"
              id="username"
              name="userName"
              label="Username"
              variant="outlined"
              placeholder="Username"
              value={formik.values.userName}
              onChange={formik.handleChange}
              error={formik.touched.userName && Boolean(formik.errors.userName)}
              helperText={formik.touched.userName && formik.errors.userName}
            />
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="label-txt">Email</Typography>
            <Box sx={{ width: "calc(100% - 180px)" }}>
              <Typography>Primary</Typography>
              {emails.map((item, index) => (
                <AlternativeEmailField
                  name="altEmails"
                  index={index}
                  key={index}
                  selectedValue={selectedEmail}
                  emails={emails}
                  onRadioChange={(e: any) =>
                    handleSelectedEmailChange(e, index)
                  }
                  altEmail={item?.emailAddress}
                  onEmailChange={(e: any) => onEmailChange(e, index)}
                  onDelete={() => deleteEmailCLick(index)}
                  isEmailEmptyError={isEmailEmptyError}
                />
              ))}
              {emails?.length < 4 && (
                <Button
                  sx={{ mt: 2 }}
                  variant="text"
                  startIcon={<AddOutlinedIcon />}
                  onClick={addEmailClick}
                >
                  Add Email
                </Button>
              )}
            </Box>
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="label-txt">Phone</Typography>
            <Box sx={{ width: "calc(100% - 180px)" }}>
              <Typography>Primary</Typography>
              {phones.map((item, index) => (
                <AlternativePhoneField
                  name="altPhones"
                  index={index}
                  key={index}
                  selectedValue={selectedPhone}
                  phones={phones}
                  onRadioChange={(e: any) =>
                    handleSelectedPhoneChange(e, index)
                  }
                  altPhone={item?.phoneNumber}
                  onPhoneChange={(e: any) => onPhoneChange(e, index)}
                  onDelete={() => deletePhoneClick(index)}
                  isPhoneEmptyError={isPhoneEmptyError}
                />
              ))}
              {phones?.length < 4 && (
                <Button
                  sx={{ mt: 2 }}
                  variant="text"
                  startIcon={<AddOutlinedIcon />}
                  onClick={addPhoneClick}
                >
                  Add Phone
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </form>
      {showLoading && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
    </DialogContainer>
  );
}
